import { useState, Fragment } from 'react';
import { Topbar, Sidebar, Button, AccountIndex, ApplicationIndex, CreditIndex, FilesIndex, Loader, BreadCrump, Flag, FlagBanner, BankAnalysisIndex, MoMoAnalysisIndex, RiskReviewIndex } from "../../components";
import EvaluationPanel from './Evaluation/EvaluationPanel';
import { Tab, TabGroup, TabList, TabPanel, TabPanels, Dialog, Transition } from '@headlessui/react';
import { fetchRequests } from "../../Requests/getRequests";
import { useSelector, useDispatch } from "react-redux";
import { selectAccessToken, selectAdminRole, selectPermissions } from "../../slices/staffSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { authStaff } from "../../utils/constants";
import useConfigOptions from "../../hooks/useConfigOptions";
import { show } from '../../slices/toastSlice';
import { useLoan } from '../../hooks';
import { HugeiconsIcon } from '@hugeicons/react';
import { Flag01Icon } from '@hugeicons/core-free-icons';

function Index() {

    const navigate = useNavigate();
    const accessToken = useSelector(selectAccessToken);
    const adminRole = useSelector(selectAdminRole);
    const permissions = useSelector(selectPermissions);
    // Only roles with clients:blacklist (PM/HOC/CRM/HOR/admin) may flag accounts.
    const canFlag = permissions?.includes('*:*') || permissions?.includes('clients:blacklist');
    const dispatch = useDispatch();
    const { adminRoles, loanRejectionReasons } = useConfigOptions();
    const location = useLocation();
    const { pathname } = location;
    const path = pathname.split("/");
    const loanType = path[1];
    const loanId = path[2];
    const [comments, setComments] = useState("");
    const [dateTime, setDateTime] = useState("");
    const [approveProcess, setApproveProcess] = useState(false);
    const [disapproveProcess, setDisapproveProcess] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [reason, setReason] = useState("");

    function closeModal() { setIsOpen(false) }
    function openModal() { setIsOpen(true) }

    const [isModalOpen, setIsModalOpen] = useState(false);
    const isFlagModalOpen = () => setIsModalOpen(!isModalOpen);
    const isFlagModalClosed = () => setIsModalOpen(!isModalOpen);

    const { isLoading, loan } = useLoan(
        { loanId, loanType, state: null, approved: false, active: false, completed: false }
    );

    const handleSetReason = (e) => setReason(e.target.value);

    const approveLoanRequest = async (e) => {
        e.preventDefault();
        setApproveProcess(true);

        const form = document.getElementById("app-form");
        const data = new FormData(form);
        let newObj = {};
        for (let [key, value] of data) { newObj[key] = value; }

        let res = await fetchRequests(`/loans/staff/approveRejectLoan`, "POST", authStaff(accessToken), {}, {
            _id: loanId, type: loanType, approved: true, ...newObj, adminRole
        });

        if (res?.code === 401) {
            sessionStorage.clear();
            navigate("/login");
            dispatch(show({ type: "error", state: true, message: "Your session has expired. Please login to continue." }));
            return;
        }
        if (res?.err) {
            dispatch(show({ type: "error", state: true, message: res?.err }));
        } else {
            dispatch(show({ type: "success", state: true, message: `${res?.message}` }));
            setTimeout(() => navigate("/loans"), 1500);
        }
        setApproveProcess(false);
    };

    const rejectLoanRequest = async (e) => {
        e.preventDefault();
        setDisapproveProcess(true);

        const form = document.getElementById("app-form");
        const data = new FormData(form);
        let newObj = {};
        for (let [key, value] of data) { newObj[key] = value; }

        let res = await fetchRequests(`/loans/staff/rejectRejectLoan`, "POST", authStaff(accessToken), {}, {
            _id: loanId, type: loanType, approved: false, ...newObj, adminRole, comments, reason, dateTime
        });
        setDisapproveProcess(false);

        if (res?.code === 401) {
            sessionStorage.clear();
            navigate("/login");
            dispatch(show({ type: "error", state: true, message: "Your session has expired. Please login to continue." }));
            return;
        }
        if (res?.err) {
            dispatch(show({ type: "error", state: true, message: res?.err }));
            return;
        }
        dispatch(show({ type: "error", state: true, message: res?.message }));
        setTimeout(() => navigate("/loans"), 1200);
    };

    return (
        <div className='main-container'>
            <Topbar />
            <Sidebar />

            <main className='main-content'>
                <section className='section-container space-y-5'>

                    {loan ?
                        <>
                        <div className="flex items-center justify-between">
                            <BreadCrump
                                path='/loans'
                                main="Loans"
                                sub={isLoading ? <Loader color={"text-gray-700"} size={"w-4 h-4 ml-1"} /> : `${loan?.type} — ${loan?._id}`}
                            />

                            {canFlag && (
                                <button
                                    type='button'
                                    onClick={isFlagModalOpen}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-500 border border-red-100 rounded-lg bg-red-50/60 hover:bg-red-50 transition-colors"
                                >
                                    <HugeiconsIcon icon={Flag01Icon} size={14} />
                                    Flag
                                </button>
                            )}
                        </div>

                        {loan?.clientFlagged && Object.keys(loan.clientFlagged).length > 0 && (
                            <FlagBanner clientFlagged={loan.clientFlagged} />
                        )}

                        <TabGroup>
                            <TabList className="flex border-b border-gray-200 bg-white rounded-t-xl">
                                {["Application", "Evaluation", "Bank Review", "MoMo Review", "Risk Review", "Credit Bureau", "Files", "Loan Account"].map((label) => (
                                    <Tab
                                        key={label}
                                        className={({ selected }) =>
                                            `shrink-0 px-6 py-3.5 text-sm font-medium border-b-2 -mb-px transition-colors focus:outline-none whitespace-nowrap ${
                                                selected
                                                    ? 'border-[#1e3a6e] text-[#1e3a6e]'
                                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            }`
                                        }
                                    >
                                        {label}
                                    </Tab>
                                ))}
                            </TabList>

                            <form id='app-form'>
                                <TabPanels className="mt-0">
                                    <TabPanel><ApplicationIndex loan={loan} /></TabPanel>
                                    <TabPanel><EvaluationPanel loan={loan} /></TabPanel>
                                    <TabPanel><BankAnalysisIndex loanId={loan?._id} loanType={loan?.type || loan?.loanType} /></TabPanel>
                                    <TabPanel><MoMoAnalysisIndex loanId={loan?._id} loanType={loan?.type || loan?.loanType} /></TabPanel>
                                    <TabPanel><RiskReviewIndex loanId={loan?._id} loanType={loan?.type || loan?.loanType} pensionState={loan?.pensionState || ''} /></TabPanel>
                                    <TabPanel><CreditIndex loan={loan} /></TabPanel>
                                    <TabPanel><FilesIndex loan={loan} /></TabPanel>
                                    <TabPanel><AccountIndex loan={loan} /></TabPanel>
                                </TabPanels>

                                <div className='mt-5 bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4'>
                                    {loan?.adminStage === adminRoles.pm && (
                                        <div>
                                            <label htmlFor="overallAssessment" className='form-label mb-1.5'>
                                                Overall Assessment
                                            </label>
                                            <textarea
                                                name="overallAssessment"
                                                id="overallAssessment"
                                                rows={3}
                                                placeholder="Indicate why the loan should be approved"
                                                defaultValue={loan?.overallAssessment || ""}
                                                disabled={!!loan?.overallAssessment}
                                                className="w-full text-sm border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 placeholder-gray-400 disabled:bg-gray-50 disabled:text-gray-500"
                                            />
                                        </div>
                                    )}

                                    <div>
                                        <label htmlFor="comment" className='block text-sm font-medium text-gray-700 mb-1.5'>
                                            Comments
                                        </label>
                                        <textarea
                                            name="comment"
                                            id="comment"
                                            rows={4}
                                            className="w-full text-sm border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 placeholder-gray-400"
                                            placeholder="Add any comments…"
                                            onChange={(e) => setComments(e.target.value)}
                                        />
                                    </div>

                                    <div className='flex items-center gap-3'>
                                        <Button
                                            btnText="Approve"
                                            btnType="button"
                                            btnClasses="form-button w-auto px-6"
                                            processing={approveProcess}
                                            btnFunction={approveLoanRequest}
                                        />
                                        <Button
                                            btnText="Reject"
                                            btnType="button"
                                            btnClasses="form-button-default w-auto px-6"
                                            btnFunction={openModal}
                                        />
                                    </div>
                                </div>
                            </form>
                        </TabGroup>
                        </>
                    :
                    <div className='w-full py-12 inline-flex justify-center items-center'>
                        <Loader color={"text-gray-500"} size={"w-6 h-6"} />
                    </div>
                    }

                </section>
            </main>

            {/* Rejection dialog */}
            <Transition appear show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={closeModal}>
                    <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 space-y-5">
                                    <div>
                                        <Dialog.Title as="h3" className="text-base font-semibold text-gray-900">
                                            Reject Application
                                        </Dialog.Title>
                                        <p className="mt-1 text-sm text-gray-500">
                                            Select a reason — this will be sent to the client.
                                        </p>
                                    </div>

                                    <div className="space-y-2">
                                        {loanRejectionReasons.map((r, index) => (
                                            <label key={index} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
                                                <input
                                                    type="radio"
                                                    name="rejectionReason"
                                                    value={r.value}
                                                    className="h-4 w-4 accent-red-500 shrink-0"
                                                    onChange={handleSetReason}
                                                />
                                                <span className="text-sm text-gray-700">{r.text}</span>
                                            </label>
                                        ))}
                                    </div>

                                    <div>
                                        <label htmlFor="rejectedComment" className='block text-sm font-medium text-gray-700 mb-1.5'>
                                            Comments
                                        </label>
                                        <textarea
                                            name="rejectedComment"
                                            id="rejectedComment"
                                            rows={3}
                                            className="w-full text-sm border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 placeholder-gray-400"
                                            onChange={(e) => setComments(e.target.value)}
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="dateTime" className='block text-sm font-medium text-gray-700 mb-1.5'>
                                            Date &amp; Time of Call to Client
                                        </label>
                                        <input
                                            type="datetime-local"
                                            name="dateTime"
                                            id="dateTime"
                                            className='form-input'
                                            onChange={(e) => setDateTime(e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div className="flex items-center gap-3 pt-1">
                                        <Button
                                            btnText="Reject"
                                            btnType="button"
                                            btnClasses="danger-button"
                                            btnFunction={rejectLoanRequest}
                                            processing={disapproveProcess}
                                        />
                                        <Button
                                            btnText="Cancel"
                                            btnType="button"
                                            btnClasses="cancel-button"
                                            btnFunction={closeModal}
                                        />
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            <Flag
                isOpen={isModalOpen}
                isClosed={isFlagModalClosed}
                client={loan?.client}
            />
        </div>
    );
}

export default Index;
